import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { authService, UserProfile } from '../services/auth';
import { categoriasService, Categoria } from '../services/categorias';
import { entidadesService, Entidade } from '../services/entidades';
import { asaasService } from '../services/asaas';
import { configuracoesService, AppConfigs } from '../services/configuracoes';
import { notificationsService, NotificationLog } from '../services/notifications';

export const Configuracoes: React.FC = () => {
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line
  const initialTab = searchParams.get('tab') as 'categorias' | 'entidades' | 'seguranca' | 'notificacoes' || 'categorias';
  const [activeTab, setActiveTab] = useState<'categorias' | 'entidades' | 'seguranca' | 'notificacoes'>(initialTab);

  // Estados do Perfil
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);

  // Estados de Dados
  const [categories, setCategories] = useState<Categoria[]>([]);
  const [entities, setEntities] = useState<Entidade[]>([]);
  const [appConfigs, setAppConfigs] = useState<AppConfigs>({});
  const [isSavingConfigs, setIsSavingConfigs] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState<'open' | 'close' | 'connecting' | 'unconfigured' | 'unknown' | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Estados QR Code / EVO Interaction
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [isGeneratingQr, setIsGeneratingQr] = useState(false);

  // Estados Pairing Code
  const [showPairingModal, setShowPairingModal] = useState(false);
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [pairingPhone, setPairingPhone] = useState('');
  const [isGeneratingPairing, setIsGeneratingPairing] = useState(false);

  // Estados Notificações (envio manual + logs)
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [notificationLogs, setNotificationLogs] = useState<NotificationLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const loadNotificationLogs = async () => {
    setLoadingLogs(true);
    try {
      const logs = await notificationsService.getLogs(10);
      setNotificationLogs(logs);
    } catch (err) {
      console.error('Erro ao carregar logs de notificação:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Carrega logs quando o usuário entra na aba de notificações
  useEffect(() => {
    if (activeTab === 'notificacoes') {
      loadNotificationLogs();
    }
  }, [activeTab]);

  const handleSendReminderNow = async () => {
    if (!window.confirm('Disparar agora um lembrete de pagamento via WhatsApp para o número configurado?')) {
      return;
    }
    setIsSendingReminder(true);
    try {
      const res = await notificationsService.runDaily();
      if (res.sucesso && res.sent) {
        showNotification(`Lembrete enviado! ${res.despesas_count ?? 0} despesa(s) listada(s).`);
      } else if (!res.sent) {
        const reasonLabels: Record<string, string> = {
          nada_para_enviar: 'Nenhuma despesa pendente — nada a enviar.',
          numero_nao_configurado: 'Número WhatsApp não configurado.',
          whatsapp_desconectado: 'WhatsApp desconectado — conecte primeiro.',
        };
        showNotification(reasonLabels[res.reason || ''] || 'Nada enviado.', 'error');
      } else {
        showNotification(`Falha no envio: ${res.erro || 'erro desconhecido'}`, 'error');
      }
      // Recarrega a lista de logs para mostrar o resultado
      loadNotificationLogs();
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Erro ao disparar lembrete', 'error');
    } finally {
      setIsSendingReminder(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [u, c, e, configs] = await Promise.all([
        authService.getProfile().catch(() => null),
        categoriasService.list().catch(() => []),
        entidadesService.list().catch(() => []),
        configuracoesService.getConfigs().catch(() => ({}))
      ]);
      if (u) {
        setProfile(u);
        setUserAvatar(u.avatar || null);
      }
      setCategories(c);
      setEntities(e);
      setAppConfigs(configs);
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
      showNotification('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Polling de status do WhatsApp quando aba notificacoes está ativa
  useEffect(() => {
    if (activeTab !== 'notificacoes') return;
    
    const checkStatus = async () => {
      try {
        const res = await configuracoesService.getWhatsAppStatus();
        setWhatsappStatus(res.state as any);
        
        // Se o QR Modal estiver aberto e o status mudar para 'open', fecha o modal
        if (res.state === 'open' && showQrModal) {
          setShowQrModal(false);
          setQrCode(null);
          showNotification('WhatsApp conectado com sucesso!');
        }
      } catch {
        setWhatsappStatus('unknown');
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, showQrModal ? 3000 : 30000); // Polling rápido se estiver no modal de pareamento
    return () => clearInterval(interval);
  }, [activeTab, showQrModal]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const { avatarUrl } = await authService.uploadAvatar(file);
        setUserAvatar(avatarUrl);
        // Atualiza o perfil localmente também
        if (profile) setProfile({ ...profile, avatar: avatarUrl });
      } catch (err) {
        showNotification('Erro ao enviar avatar', 'error');
      }
    }
  };

  const handleSaveProfile = async () => {
    if (!profile) return;
    try {
      await authService.updateProfile({
        nome: profile.nome,
        email: profile.email,
        role: profile.role
      });
      setIsEditingProfile(false);
      showNotification('Perfil atualizado com sucesso!');
    } catch (err) {
      showNotification('Erro ao salvar perfil', 'error');
    }
  };

  const [newCategory, setNewCategory] = useState('');
  const [newType, setNewType] = useState('despesa');

  const [newEntityName, setNewEntityName] = useState('');
  const [newEntityDocument, setNewEntityDocument] = useState('');
  const [newEntityType, setNewEntityType] = useState('cliente');

  const handleAddEntity = async () => {
    if (newEntityName.trim()) {
      try {
        await entidadesService.create({
          nome: newEntityName,
          documento: newEntityDocument,
          tipo: newEntityType as any
        });
        setNewEntityName('');
        setNewEntityDocument('');
        loadData();
        showNotification('Entidade criada com sucesso!');
      } catch (err) {
        showNotification('Erro ao criar entidade', 'error');
      }
    }
  };

  const deleteEntity = async (id: number) => {
    if (window.confirm('Deseja excluir esta entidade?')) {
      try {
        await entidadesService.delete(id);
        loadData();
        showNotification('Entidade excluída!');
      } catch (err) {
        showNotification('Erro ao excluir entidade', 'error');
      }
    }
  };

  const handleAddCategory = async () => {
    if (newCategory.trim()) {
      try {
        await categoriasService.create({
          nome: newCategory,
          grupo: newType as any
        });
        setNewCategory('');
        loadData();
        showNotification('Categoria criada!');
      } catch (err) {
        showNotification('Erro ao criar categoria', 'error');
      }
    }
  };

  const deleteCategory = async (id: number, name: string) => {
    if (window.confirm(`Tem certeza que deseja excluir a categoria "${name}"?`)) {
      try {
        await categoriasService.delete(id);
        loadData();
        showNotification('Categoria excluída!');
      } catch (err) {
        showNotification('Erro ao excluir categoria', 'error');
      }
    }
  };

  const toggleCategory = async (id: number, active: number) => {
    try {
      await categoriasService.update(id, { ativo: active ? 0 : 1 });
      loadData();
      showNotification('Status atualizado!');
    } catch (err) {
      showNotification('Erro ao atualizar status', 'error');
    }
  };

  const handleSaveConfigs = async () => {
    setIsSavingConfigs(true);
    try {
      await configuracoesService.saveConfigs(appConfigs);
      showNotification('Configurações salvas!');
    } catch (err) {
      showNotification('Erro ao salvar configurações', 'error');
    } finally {
      setIsSavingConfigs(false);
    }
  };

  const handleConnectWhatsApp = async () => {
    setIsGeneratingQr(true);
    setQrCode(null);
    try {
      const instanceName = appConfigs.evolution_instance || 'dre';

      // 1. Tenta CRIAR a instância — retorna QR code direto na resposta
      let qrData: string | null = null;
      try {
        const createRes = await configuracoesService.createEvolutionInstance(instanceName);
        if (createRes.base64) {
          qrData = createRes.base64;
        }
      } catch (createErr: any) {
        const errMsg = createErr.response?.data?.error || '';
        if (!errMsg.toLowerCase().includes('already exists') && createErr.response?.status !== 400) {
          throw createErr;
        }
      }

      // 2. Se não veio QR na criação (instância já existia), busca via connect
      if (!qrData) {
        const connectRes = await configuracoesService.getEvolutionQrCode();
        qrData = connectRes.base64 || null;
      }

      if (qrData) {
        const qr = qrData.startsWith('data:') ? qrData : `data:image/png;base64,${qrData}`;
        setQrCode(qr);
        setShowQrModal(true);
        configuracoesService.getConfigs().then(setAppConfigs).catch(() => {});
      } else {
        showNotification('Não foi possível obter o QR Code. Verifique o nome da conexão.', 'error');
      }
    } catch (err: any) {
      console.error(err);
      const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao conectar com Evolution API';
      showNotification(msg, 'error');
    } finally {
      setIsGeneratingQr(false);
    }
  };

  const handleGetPairingCode = async () => {
    const phone = pairingPhone || appConfigs.whatsapp_number || '';
    if (!phone) {
      showNotification('Informe o número de telefone do WhatsApp (com DDD).', 'error');
      return;
    }
    setIsGeneratingPairing(true);
    setPairingCode(null);
    try {
      const res = await configuracoesService.getEvolutionPairingCode(phone);
      const code = res.code ?? res.pairingCode ?? res.data?.code ?? null;
      if (code) {
        setPairingCode(code);
      } else {
        showNotification('Código não retornado pela API. Verifique se a instância está iniciada.', 'error');
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Erro ao gerar código de pareamento';
      showNotification(msg, 'error');
    } finally {
      setIsGeneratingPairing(false);
    }
  };

  const handleTestWhatsApp = async () => {
    if (whatsappStatus !== 'open') {
      showNotification('WhatsApp não está conectado. Conecte primeiro.', 'error');
      return;
    }
    try {
      const res = await configuracoesService.testWhatsApp();
      showNotification(res.message || 'Mensagem enviada!');
    } catch (err: any) {
      showNotification(err.response?.data?.error || 'Erro ao testar WhatsApp', 'error');
    }
  };

  const getCategoryTypeLabel = (tipo: string) => {
    const labels: Record<string, string> = {
      receita: 'Receitas',
      custo: 'Custos Variáveis',
      despesa: 'Despesas Fixas',
    };
    return labels[tipo] || tipo;
  };

  const getCategoryTypeIcon = (tipo: string) => {
    const icons: Record<string, string> = {
      receita: 'trending_up',
      custo: 'shopping_cart',
      despesa: 'account_balance',
    };
    return icons[tipo] || 'label';
  };

  const groupedCategories = categories.reduce((acc, cat) => {
    if (!acc[cat.grupo]) {
      acc[cat.grupo] = [];
    }
    acc[cat.grupo].push(cat);
    return acc;
  }, {} as Record<string, typeof categories>);

  const categoryOrder = ['receita', 'custo', 'despesa'];

  const tabs = [
    { id: 'categorias', label: 'Plano de Contas', icon: 'account_tree' },
    { id: 'entidades', label: 'Clientes & Fornecedores', icon: 'group' },
    { id: 'notificacoes', label: 'Notificações & WhatsApp', icon: 'send_to_mobile' },
    { id: 'seguranca', label: 'Perfil & Conta', icon: 'person' },
  ];

  return (
    <Layout pageTitle="Configurações" breadcrumb="Aplicação">
      {/* Toast Notification */}
      {notification && (
        <div className={`fixed top-6 right-6 z-[9999] animate-fade-in-right`}>
          <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border backdrop-blur-xl ${
            notification.type === 'success' 
            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' 
            : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
          }`}>
            <span className="material-symbols-outlined">
              {notification.type === 'success' ? 'check_circle' : 'error'}
            </span>
            <span className="font-bold text-sm">{notification.message}</span>
          </div>
        </div>
      )}
      <div className="space-y-8 animate-fade-in-up">
        {/* Title */}
        <section className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-white">Ajustes do App</h2>
            <p className="text-slate-500 mt-1">Gerencie categorias, entidades e preferências da plataforma.</p>
          </div>
        </section>

        {/* Settings Layout with Vertical Tabs */}
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Menu Lateral das Configurações */}
          <div className="w-full lg:w-72 flex-shrink-0">
            <div className="glass-panel p-2 rounded-2xl border-slate-800/50 flex flex-col gap-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === tab.id
                    ? 'bg-slate-800/80 text-white shadow-md'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/30'
                    }`}
                >
                  <span className={`material-symbols-outlined text-[20px] ${activeTab === tab.id ? 'text-primary' : 'text-slate-500'}`}>
                    {tab.icon}
                  </span>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Conteúdo Dinâmico */}
          <div className="flex-1 space-y-6">

            {/* TAB CATEGORIAS */}
            {activeTab === 'categorias' && (
              <div className="animate-fade-in-up space-y-6">
                <div className="glass-panel rounded-2xl p-6 border-slate-800/50 bg-slate-800/10">
                  <h3 className="font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2 text-slate-400">
                    <span className="material-symbols-outlined text-[18px]">add_circle</span>
                    Nova Categoria
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input
                      type="text"
                      placeholder="Nome da categoria..."
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                      className="flex-[2] px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500/50 transition-all font-medium"
                    />
                    <select
                      value={newType}
                      onChange={(e) => setNewType(e.target.value)}
                      className="flex-1 px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/50 transition-all cursor-pointer font-medium"
                    >
                      <option value="receita">Receita</option>
                      <option value="custo">Custo Variável</option>
                      <option value="despesa">Despesa Fixa</option>
                    </select>
                    <button
                      onClick={handleAddCategory}
                      className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-bold transition-all md:w-auto w-full"
                    >
                      Salvar
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {categoryOrder.map((typeKey) => {
                    const typedCategories = groupedCategories[typeKey];
                    if (!typedCategories) return null;

                    return (
                      <div key={typeKey} className="glass-panel rounded-2xl border-slate-800/50 overflow-hidden flex flex-col h-full">
                        <div className="p-5 border-b border-slate-800/80 bg-slate-800/20">
                          <h4 className="text-sm font-bold uppercase tracking-wider text-slate-300 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[18px] text-primary">{getCategoryTypeIcon(typeKey)}</span>
                            {getCategoryTypeLabel(typeKey)}
                          </h4>
                        </div>
                        <div className="p-2 space-y-1 flex-1 bg-slate-900/10">
                          {typedCategories.map((cat) => (
                            <div
                              key={cat.id}
                              className={`flex items-center justify-between p-3 rounded-xl hover:bg-slate-800/40 transition-colors group ${!cat.ativo ? 'opacity-50' : ''}`}
                            >
                              <div className="flex items-center gap-3">
                                {cat.pre_carregada ? (
                                  <span className="material-symbols-outlined text-slate-600 text-[18px]">lock</span>
                                ) : (
                                  <span className="material-symbols-outlined text-slate-500 text-[18px]">label</span>
                                )}
                                <span className={`text-sm font-semibold text-slate-300 group-hover:text-white transition-colors ${!cat.ativo ? 'line-through decoration-slate-600' : ''}`}>
                                  {cat.nome}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => toggleCategory(cat.id, cat.ativo)}
                                  className={`text-[11px] font-bold uppercase px-2 py-1 rounded-md transition-colors border ${cat.ativo ? 'bg-slate-800 border-slate-600 text-slate-300' : 'bg-primary/20 border-primary/30 text-primary'}`}
                                >
                                  {cat.ativo ? 'Ocultar' : 'Exibir'}
                                </button>
                                {!cat.pre_carregada && (
                                  <button onClick={() => deleteCategory(cat.id, cat.nome)} className="text-slate-500 hover:text-rose-500 transition-colors p-1">
                                    <span className="material-symbols-outlined text-[18px]">delete</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TAB ENTIDADES */}
            {activeTab === 'entidades' && (
              <div className="animate-fade-in-up space-y-6">
                <div className="glass-panel rounded-2xl p-6 border-slate-800/50 bg-slate-800/10">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-sm uppercase tracking-wider flex items-center gap-2 text-slate-400">
                      <span className="material-symbols-outlined text-[18px]">person_add</span>
                      Novo Cadastro Manual
                    </h3>
                    <button
                      onClick={async () => {
                        try {
                          await asaasService.syncCustomers();
                          loadData();
                          showNotification('Sincronização com Asaas concluída!');
                        } catch (err) {
                          showNotification('Erro ao sincronizar com Asaas', 'error');
                        }
                      }}
                      className="flex items-center gap-2 bg-slate-800/80 hover:bg-slate-700 border border-slate-700/50 rounded-xl px-4 py-2 text-[11px] font-bold text-slate-300 hover:text-white transition-all duration-300"
                    >
                      <span className="material-symbols-outlined text-[16px] text-primary">sync</span>
                      Sincronizar Clientes Asaas
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-5">
                      <input
                        type="text"
                        placeholder="Nome da Entidade..."
                        value={newEntityName}
                        onChange={(e) => setNewEntityName(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500/50 transition-all font-medium"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <input
                        type="text"
                        placeholder="CPF/CNPJ (Opcional)"
                        value={newEntityDocument}
                        onChange={(e) => setNewEntityDocument(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500/50 transition-all font-medium"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <select
                        value={newEntityType}
                        onChange={(e) => setNewEntityType(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500/50 transition-all cursor-pointer font-medium"
                      >
                        <option value="cliente">Cliente</option>
                        <option value="fornecedor">Fornecedor</option>
                        <option value="ambos">Ambos</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <button
                        onClick={handleAddEntity}
                        className="w-full h-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2"
                      >
                        Salvar
                      </button>
                    </div>
                  </div>
                </div>

                <div className="glass-panel rounded-2xl border-slate-800/50 overflow-hidden">
                  <table className="w-full text-left whitespace-nowrap">
                    <thead>
                      <tr className="border-b border-slate-800/80 bg-slate-800/30">
                        <th className="p-5 text-xs font-bold text-slate-500 uppercase tracking-wider">Entidade</th>
                        <th className="p-5 text-xs font-bold text-slate-500 uppercase tracking-wider">Documento</th>
                        <th className="p-5 text-xs font-bold text-slate-500 uppercase tracking-wider">Tipo</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/30">
                      {entities.map((ent) => (
                        <tr key={ent.id} className="hover:bg-slate-800/20 transition-colors group">
                          <td className="p-5">
                            <div className="flex items-center gap-3">
                              <span className="material-symbols-outlined text-slate-500 text-[20px] bg-slate-800 p-1.5 rounded-lg">
                                {ent.tipo === 'cliente' ? 'person' : ent.tipo === 'fornecedor' ? 'storefront' : 'swap_horiz'}
                              </span>
                              <div>
                                <p className="text-sm font-bold text-slate-200 group-hover:text-white transition-colors">{ent.nome}</p>
                                {ent.asaas_id && (
                                  <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-primary/10 border border-primary/20 text-primary">
                                    <span className="material-symbols-outlined text-[10px]">link</span> ID Sincronizado
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-5 text-sm text-slate-400 font-medium">
                            {ent.documento || 'Nenhum Cadastrado'}
                          </td>
                          <td className="p-5">
                            <span className="bg-slate-800 border border-slate-700/50 px-2 py-1 rounded-md text-xs font-bold text-slate-400 capitalize">
                              {ent.tipo}
                            </span>
                          </td>
                          <td className="p-5 text-right w-10 relative">
                            <button
                              onClick={() => deleteEntity(ent.id)}
                              className="text-slate-600 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all absolute right-5 top-1/2 -translate-y-1/2"
                            >
                              <span className="material-symbols-outlined text-[20px]">delete</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {entities.length === 0 && (
                    <div className="p-8 text-center text-slate-500 text-sm font-medium">Nenhum registro encontrado.</div>
                  )}
                </div>
              </div>
            )}

            {/* TAB SEGURANÇA */}
            {activeTab === 'seguranca' && (
              <div className="animate-fade-in-up space-y-6">
                <div className="glass-panel rounded-2xl p-6 md:p-8 border-slate-800/50">
                  <h3 className="font-bold text-lg mb-6 flex items-center gap-3 text-white border-b border-slate-800/50 pb-4">
                    <div className="p-2 bg-slate-800/80 rounded-lg">
                      <span className="material-symbols-outlined text-primary text-[20px]">person</span>
                    </div>
                    Informações do Perfil
                  </h3>
                  <div className="flex flex-col md:flex-row gap-8">
                    {/* Avatar Section */}
                    <div className="flex flex-col items-center gap-4">
                      <div className="relative group">
                        <div className="w-32 h-32 rounded-2xl bg-slate-800 border-2 border-slate-700/50 flex items-center justify-center overflow-hidden shadow-xl transition-all group-hover:border-primary/50">
                          {userAvatar ? (
                            <img src={userAvatar} alt="Avatar" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-4xl font-black text-slate-600">
                              {profile?.nome?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() || '??'}
                            </span>
                          )}
                          <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center cursor-pointer">
                            <span className="material-symbols-outlined text-white text-[24px]">photo_camera</span>
                            <span className="text-[10px] font-bold text-white uppercase mt-1">Alterar Foto</span>
                            <input type="file" className="hidden" accept="image/*" onChange={handleAvatarChange} />
                          </label>
                        </div>
                      </div>
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Avatar do Sistema</p>
                    </div>

                    <div className="flex-1 space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label className="text-xs uppercase font-bold text-slate-500 mb-1 block tracking-wider">Nome Completo</label>
                          {isEditingProfile ? (
                            <input
                              type="text"
                              value={profile?.nome || ''}
                              onChange={(e) => setProfile(prev => prev ? { ...prev, nome: e.target.value } : null)}
                              className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                            />
                          ) : (
                            <p className="text-base font-bold text-slate-200">{profile?.nome}</p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs uppercase font-bold text-slate-500 mb-1 block tracking-wider">E-mail Corporativo</label>
                          {isEditingProfile ? (
                            <input
                              type="email"
                              value={profile?.email || ''}
                              onChange={(e) => setProfile(prev => prev ? { ...prev, email: e.target.value } : null)}
                              className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                            />
                          ) : (
                            <p className="text-base font-bold text-slate-200">{profile?.email}</p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs uppercase font-bold text-slate-500 mb-1 block tracking-wider">Função</label>
                          {isEditingProfile ? (
                            <input
                              type="text"
                              value={profile?.role || ''}
                              onChange={(e) => setProfile(prev => prev ? { ...prev, role: e.target.value } : null)}
                              className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                            />
                          ) : (
                            <p className="text-base font-bold text-slate-200">{profile?.role}</p>
                          )}
                        </div>
                      </div>
                      <div className="pt-2 flex justify-end">
                        <button
                          onClick={isEditingProfile ? handleSaveProfile : () => setIsEditingProfile(true)}
                          className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${isEditingProfile
                            ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                            : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                            }`}
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            {isEditingProfile ? 'check_circle' : 'edit'}
                          </span>
                          {isEditingProfile ? 'Salvar Alterações' : 'Atualizar Dados'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="glass-panel rounded-2xl p-6 md:p-8 border-l-4 border-l-rose-500/80 border-t-slate-800/50 border-r-slate-800/50 border-b-slate-800/50 bg-slate-900/40">
                  <h3 className="font-bold text-lg mb-6 flex items-center gap-3 text-rose-500/90 border-b border-slate-800/50 pb-4">
                    <div className="p-2 bg-rose-500/10 rounded-lg">
                      <span className="material-symbols-outlined text-rose-500 text-[20px]">warning</span>
                    </div>
                    Zona de Perigo
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button className="flex-1 px-4 py-3 border border-rose-500/20 text-rose-500/90 rounded-xl font-bold hover:bg-rose-500/10 transition-all text-sm flex items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-[18px]">logout</span>
                      Desconectar Dispositivos
                    </button>
                    <button className="flex-1 px-4 py-3 bg-rose-500/10 border border-rose-500/20 text-rose-500/90 rounded-xl font-bold hover:bg-rose-500/20 hover:text-rose-400 transition-all text-sm flex items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-[18px]">delete_forever</span>
                      Deletar Conta
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* TAB NOTIFICAÇÕES */}
            {activeTab === 'notificacoes' && (
              <div className="animate-fade-in-up space-y-6">
                <div className="glass-panel rounded-2xl p-6 md:p-8 border-slate-800/50">
                  <h3 className="font-bold text-lg mb-6 flex items-center gap-3 text-white border-b border-slate-800/50 pb-4">
                    <div className="p-2 bg-slate-800/80 rounded-lg">
                      <span className="material-symbols-outlined text-primary text-[20px]">send_to_mobile</span>
                    </div>
                    Integração Evolution API (WhatsApp)
                    {/* Status Badge */}
                    <span className={`ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full border ${
                      whatsappStatus === 'open'
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                        : whatsappStatus === 'connecting'
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                        : whatsappStatus === 'unconfigured' || whatsappStatus === null
                        ? 'bg-slate-700/50 border-slate-600/50 text-slate-500'
                        : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        whatsappStatus === 'open' ? 'bg-emerald-400 animate-pulse' :
                        whatsappStatus === 'connecting' ? 'bg-amber-400 animate-pulse' :
                        whatsappStatus === null ? 'bg-slate-500' : 'bg-rose-400'
                      }`} />
                      {whatsappStatus === 'open' ? 'Conectado'
                        : whatsappStatus === 'connecting' ? 'Conectando...'
                        : whatsappStatus === 'unconfigured' ? 'Não configurado'
                        : whatsappStatus === null ? 'Verificando...'
                        : 'Desconectado'}
                    </span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs uppercase font-bold text-slate-500 mb-1 block tracking-wider">Nome da Conexão</label>
                      <input
                        type="text"
                        value={appConfigs.evolution_instance || ''}
                        onChange={(e) => setAppConfigs({ ...appConfigs, evolution_instance: e.target.value })}
                        placeholder="Ex: chalk"
                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase font-bold text-slate-500 mb-1 block tracking-wider">Número para alertas (com DDD)</label>
                      <input
                        type="text"
                        value={appConfigs.whatsapp_number || ''}
                        onChange={(e) => setAppConfigs({ ...appConfigs, whatsapp_number: e.target.value })}
                        placeholder="5511999999999"
                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm text-white focus:ring-2 focus:ring-primary/50 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-slate-800/50 flex flex-col sm:flex-row items-start justify-between gap-4">
                    {/* Ações contextuais por estado */}
                    {whatsappStatus === 'open' ? (
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={handleTestWhatsApp}
                          className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 border border-slate-700"
                        >
                          <span className="material-symbols-outlined text-[18px]">send</span>
                          Testar Conexão
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Deseja desconectar o WhatsApp?')) return;
                            try {
                              await configuracoesService.disconnectEvolution();
                              setWhatsappStatus('close');
                              showNotification('WhatsApp desconectado.');
                            } catch {
                              showNotification('Erro ao desconectar', 'error');
                            }
                          }}
                          className="px-6 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 border border-rose-800/50 text-rose-400 hover:bg-rose-900/30"
                        >
                          <span className="material-symbols-outlined text-[18px]">link_off</span>
                          Desconectar
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={handleConnectWhatsApp}
                          disabled={isGeneratingQr}
                          className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 border border-emerald-400/50 shadow-lg shadow-emerald-500/20 disabled:opacity-60"
                        >
                          {isGeneratingQr ? (
                            <><span className="material-symbols-outlined text-[18px] animate-spin">sync</span>Gerando...</>
                          ) : (
                            <><span className="material-symbols-outlined text-[18px]">qr_code_2</span>Conectar via QR Code</>
                          )}
                        </button>
                        <button
                          onClick={() => { setPairingPhone(appConfigs.whatsapp_number || ''); setPairingCode(null); setShowPairingModal(true); }}
                          disabled={isGeneratingQr}
                          className="px-6 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 border bg-slate-700 hover:bg-slate-600 text-white border-slate-600 disabled:opacity-60"
                        >
                          <span className="material-symbols-outlined text-[18px]">pin</span>
                          Conectar via Código
                        </button>
                      </div>
                    )}

                    <button
                      onClick={handleSaveConfigs}
                      disabled={isSavingConfigs}
                      className="px-8 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
                    >
                      {isSavingConfigs ? 'Salvando...' : (
                        <><span className="material-symbols-outlined text-[18px]">save</span>Salvar</>
                      )}
                    </button>
                  </div>
                </div>

                <div className="glass-panel rounded-2xl p-6 border-l-4 border-l-primary/50 bg-primary/5">
                  <div className="flex gap-4">
                    <span className="material-symbols-outlined text-primary text-[24px]">info</span>
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-slate-200">Como funciona o alerta?</p>
                      <p className="text-xs text-slate-400 leading-relaxed">
                        O sistema verifica diariamente despesas com vencimento para o dia atual e despesas atrasadas.
                        Para automatizar, configure um <strong>Cron Job</strong> no seu servidor executando:
                        <code className="mx-1 px-1.5 py-0.5 bg-slate-800 rounded text-primary">php spark notify:daily</code>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Disparo manual + auditoria de envios */}
                <div className="glass-panel rounded-2xl p-6 md:p-8 border-slate-800/50">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 pb-4 border-b border-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-slate-800/80 rounded-lg">
                        <span className="material-symbols-outlined text-primary text-[20px]">notifications_active</span>
                      </div>
                      <div>
                        <h3 className="font-bold text-base text-white">Lembrete Manual</h3>
                        <p className="text-xs text-slate-500 mt-0.5">Dispare agora o mesmo envio que o cron faria.</p>
                      </div>
                    </div>
                    <button
                      onClick={handleSendReminderNow}
                      disabled={isSendingReminder}
                      className="px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/20 disabled:opacity-60"
                    >
                      {isSendingReminder ? (
                        <><span className="material-symbols-outlined text-[18px] animate-spin">sync</span>Enviando...</>
                      ) : (
                        <><span className="material-symbols-outlined text-[18px]">send</span>Enviar Lembrete Agora</>
                      )}
                    </button>
                  </div>

                  {/* Lista dos últimos envios */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Últimos Envios</p>
                      <button
                        onClick={loadNotificationLogs}
                        disabled={loadingLogs}
                        className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-primary transition-colors flex items-center gap-1"
                      >
                        <span className={`material-symbols-outlined text-[14px] ${loadingLogs ? 'animate-spin' : ''}`}>refresh</span>
                        Atualizar
                      </button>
                    </div>

                    {loadingLogs && notificationLogs.length === 0 ? (
                      <div className="text-center py-6">
                        <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto" />
                      </div>
                    ) : notificationLogs.length === 0 ? (
                      <div className="text-center py-6 text-xs text-slate-500 italic">
                        Nenhum envio registrado ainda.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {notificationLogs.map((log) => (
                          <div
                            key={log.id}
                            className="flex items-start gap-3 p-3 rounded-xl bg-slate-800/30 border border-slate-700/30 hover:border-slate-600/50 transition-colors"
                          >
                            <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                              log.sucesso
                                ? 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]'
                                : 'bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.6)]'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-bold text-slate-300">
                                  {new Date(log.created_at.replace(' ', 'T')).toLocaleString('pt-BR')}
                                </span>
                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400 border border-slate-600/50">
                                  {log.origem}
                                </span>
                                {log.despesas_count > 0 && (
                                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                                    {log.despesas_count} despesa{log.despesas_count > 1 ? 's' : ''}
                                  </span>
                                )}
                                {log.http_code && (
                                  <span className="text-[9px] font-mono text-slate-500">HTTP {log.http_code}</span>
                                )}
                              </div>
                              {log.erro ? (
                                <p className="text-[11px] text-rose-400 mt-1 leading-snug">{log.erro}</p>
                              ) : log.mensagem_preview ? (
                                <p className="text-[11px] text-slate-500 mt-1 leading-snug line-clamp-2">
                                  {log.mensagem_preview.split('\n')[0]}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'notificacoes' && (
              <div className="text-center">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                  Powered by Evolution API & Chalk Finance
                </p>
              </div>
            )}

            {/* PAIRING CODE MODAL */}
            {showPairingModal && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
                <div className="glass-panel w-full max-w-sm rounded-3xl p-8 border-slate-800 shadow-2xl animate-fade-in-up">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-black text-white flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary">pin</span>
                      Conectar via Código
                    </h3>
                    <button
                      onClick={() => { setShowPairingModal(false); setPairingCode(null); }}
                      className="text-slate-500 hover:text-white transition-colors"
                    >
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  </div>

                  {!pairingCode ? (
                    <div className="space-y-4">
                      <p className="text-sm text-slate-400 leading-relaxed">
                        Informe o número do WhatsApp que será vinculado (com DDD, sem espaços):
                      </p>
                      <input
                        type="text"
                        value={pairingPhone}
                        onChange={(e) => setPairingPhone(e.target.value)}
                        placeholder="5541999999999"
                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded-xl px-4 py-2.5 text-sm text-white focus:ring-2 focus:ring-primary/50 outline-none"
                      />
                      <button
                        onClick={handleGetPairingCode}
                        disabled={isGeneratingPairing}
                        className="w-full px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
                      >
                        {isGeneratingPairing ? (
                          <><span className="material-symbols-outlined text-[18px] animate-spin">sync</span> Gerando...</>
                        ) : (
                          <><span className="material-symbols-outlined text-[18px]">key</span> Gerar Código</>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4 text-center">
                      <p className="text-sm text-slate-400">No WhatsApp, vá em <strong className="text-white">Aparelhos Conectados &gt; Conectar com número de telefone</strong> e digite o código abaixo:</p>
                      <div className="bg-slate-900 rounded-2xl px-6 py-5 border border-slate-700">
                        <p className="text-4xl font-black text-primary tracking-widest">{pairingCode}</p>
                      </div>
                      <div className="p-3 bg-primary/10 border border-primary/20 rounded-xl">
                        <p className="text-[11px] font-bold text-primary uppercase animate-pulse">
                          O código expira em alguns minutos
                        </p>
                      </div>
                      <button
                        onClick={() => setPairingCode(null)}
                        className="text-xs text-slate-500 hover:text-slate-300 transition-colors underline"
                      >
                        Gerar novo código
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* QR CODE MODAL OVERLAY */}
            {showQrModal && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
                <div className="glass-panel w-full max-w-sm rounded-3xl p-8 border-slate-800 shadow-2xl animate-fade-in-up">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-black text-white flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary">qr_code_2</span>
                      Conectar WhatsApp
                    </h3>
                    <button 
                      onClick={() => {
                        setShowQrModal(false);
                        setQrCode(null);
                      }}
                      className="text-slate-500 hover:text-white transition-colors"
                    >
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  </div>

                  <div className="bg-[#ffffff] p-4 rounded-2xl mb-6 shadow-inner flex items-center justify-center min-h-[250px]">
                    {qrCode ? (
                      <img src={qrCode} alt="WhatsApp QR Code" className="w-full h-auto rounded-lg" />
                    ) : (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="text-slate-500 text-xs font-bold uppercase">Carregando QR Code...</p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4 text-center">
                    <p className="text-sm text-slate-400 leading-relaxed font-medium">
                      Abra o WhatsApp no seu celular, vá em <strong className="text-white">Aparelhos Conectados</strong> e aponte a câmera para o código acima.
                    </p>
                    <div className="p-3 bg-primary/10 border border-primary/20 rounded-xl">
                      <p className="text-[11px] font-bold text-primary uppercase animate-pulse">
                        Aguardando leitura do QR Code...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
